import React, {
  useState,
  useEffect,
  useCallback,
} from 'react';
import {
  shallowEqual,
  useSelector,
  useDispatch,
} from 'react-redux';
import {
  getStatus,
} from 'redux-resource';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';
import {
  getUiState,
} from 'web-selectors';

import {
  Spinner,
  ProgramWorkoutHeader,
  WorkoutExercisesList,
  WorkoutExercisesFooter,
  WorkoutExercisesSubHeader,
} from 'web-components';

import {
  Flex,
  Text,
  WorkoutExercisesView,
} from 'web-styled';

import * as S from './styled';
import EmptyState from './EmptyState';

type Props = {
  readOnly: boolean,
  programId: number,
  resourceType: string,
};

const Workout = ({
  readOnly,
  resourceType,
  programId,
}: Props) => {
  const [activeTab, setTab] = useState('description');
  const dispatch = useDispatch();
  const workoutType = resourceType === 'programTemplates'
    ? 'WorkoutTemplate'
    : 'PersonalWorkout';
  const workoutResourceType = resourceType === 'programTemplates'
    ? 'workoutTemplates'
    : 'personalWorkouts';

  const mapState = useCallback((state) => {
    const selectedProgramWorkouts = getUiState('selectedProgramWorkouts')(state);
    const selectedWorkout = selectedProgramWorkouts[programId] || {};
    return {
      week: coreSelectors.getResourceById(
        'programWeeks',
        selectedWorkout.weekId,
      )(state),
      weeks: coreSelectors.getProgamWeeks(
        programId,
        resourceType,
      )(state),
      programWorkout: coreSelectors.getResourceById(
        'programWorkouts',
        selectedWorkout.id,
      )(state),
      workoutReadStatus: getStatus(
        state,
        [
          `${workoutResourceType}.requests.getFullWorkout.status`,
        ],
        true,
      ),
    };
  }, [programId, resourceType]);

  const {
    week,
    weeks,
    programWorkout,
    workoutReadStatus,
  } = useSelector(mapState, shallowEqual);

  useEffect(() => {
    if (programWorkout?.id) {
      dispatch(
        coreActions.requestFullWorkout({
          id: programWorkout.workout_id,
          resourceType: workoutResourceType,
        }),
      );
    }
  }, [programWorkout?.id]);

  const showList = activeTab !== 'warmup' && activeTab !== 'cooldown';
  const isEditable = showList && !readOnly;

  if (weeks.length && !programWorkout) return null;
  if (!weeks.length) {
    return (
      <EmptyState
        readOnly={readOnly}
        programId={programId}
        resourceType={resourceType}
      />
    );
  }

  return (
    <S.WorkoutWrapper>
      <Text
        size="20px"
        color="#333"
      >
        {week && week.name}
      </Text>
      <WorkoutExercisesView>
        <ProgramWorkoutHeader
          activeTab={activeTab}
          readOnly={readOnly}
          resourceType={workoutResourceType}
          programWorkout={programWorkout}
          setTab={setTab}
        />

        {isEditable && (
          <S.SubHeader>
            <WorkoutExercisesSubHeader
              workoutId={programWorkout.workout_id}
            />
          </S.SubHeader>
        )}

        {workoutReadStatus.succeeded && showList && (
          <WorkoutExercisesList
            type="workout"
            readOnly={readOnly}
            workoutId={programWorkout.workout_id}
            workoutType={workoutType}
          />
        )}

        {workoutReadStatus.pending && (
          <Flex width="100%" height="200px">
            <Spinner message="Loading exercises..." />
          </Flex>
        )}

        {isEditable && (
          <WorkoutExercisesFooter
            workoutId={programWorkout.workout_id}
            workoutType={workoutType}
          />
        )}
      </WorkoutExercisesView>
    </S.WorkoutWrapper>
  );
};

export default Workout;
